import { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { blue } from "@/theme/colors";
import {
  RING_SIZE,
  RING_STROKE_WIDTH,
  RING_RADIUS,
  RING_CIRCUMFERENCE,
  RING_GAP_LENGTH,
  PROGRESS_STATES,
} from "@/features/study-time/constants/circular-progress";

const getProgressState = (percent: number) => {
  if (percent >= 100) return PROGRESS_STATES.completed;
  if (percent >= 50) return PROGRESS_STATES.nearlyReach;
  return PROGRESS_STATES.underTarget;
};

type Props = {
  percent: number;
};

export const CircularStudyProgress: FC<Props> = ({ percent }) => {
  const { t } = useTranslation();
  const state = getProgressState(percent);
  const clampedPercent = Math.min(percent, 100);

  /* Active arc length */
  const activeLength = (clampedPercent / 100) * RING_CIRCUMFERENCE;

  /* Inactive arc: remaining after active + gap (only when not 100%) */
  const showInactive = clampedPercent < 100 && state.inactive;
  const inactiveLength = showInactive ? RING_CIRCUMFERENCE - activeLength - RING_GAP_LENGTH : 0;
  const inactiveOffset = -(activeLength + RING_GAP_LENGTH);

  return (
    <Stack sx={{ position: "relative", display: "inline-flex", width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}>
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
        {/* Inactive (background) arc */}
        {showInactive && (
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            stroke={state.inactive!}
            strokeWidth={RING_STROKE_WIDTH}
            strokeDasharray={`${inactiveLength} ${RING_CIRCUMFERENCE - inactiveLength}`}
            strokeDashoffset={inactiveOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        )}
        {/* Active (foreground) arc */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke={state.active}
          strokeWidth={RING_STROKE_WIDTH}
          strokeDasharray={`${activeLength} ${RING_CIRCUMFERENCE - activeLength}`}
          strokeDashoffset={0}
          strokeLinecap={clampedPercent > 0 ? "round" : "butt"}
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          style={{ transition: "stroke-dasharray 0.4s ease" }}
        />
      </svg>
      {/* Center label */}
      <Box
        sx={{
          // Adjust the text to be the same as UI design, with a slight offset to the top-left to optically center it within the ring
          position: "absolute",
          top: 44,
          left: 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: 120, // Prevent text from touching the edges
        }}
      >
        <Typography variant="body14Semi" color={blue[80]} textAlign="center">
          {t(state.message)}
        </Typography>
        <Typography variant="head32Bold" color={state.active}>
          {clampedPercent}%
        </Typography>
      </Box>
    </Stack>
  );
};
